import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Stack,
  TextField,
} from '@mui/material';
import SyncIcon from '@mui/icons-material/Sync';
import BoltIcon from '@mui/icons-material/Bolt';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';
import { syncSettingsApi, unifiedSyncApi, UnifiedSyncStatus } from '../services/api';
import { HelpText, SectionCard, useToast } from '@vmcom/ui';

const statusColor = (s: string): 'success' | 'warning' | 'error' | 'default' => {
  if (s === 'success') return 'success';
  if (s === 'partial_success') return 'warning';
  if (s === 'error') return 'error';
  return 'default';
};

const alertSeverity = (s: string): 'success' | 'warning' | 'error' | 'info' => {
  const c = statusColor(s);
  return c === 'default' ? 'info' : c;
};

const SyncSettings: React.FC = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [hour, setHour] = useState(8);
  const [minute, setMinute] = useState(0);
  const [syncStatus, setSyncStatus] = useState<UnifiedSyncStatus | null>(null);
  const [starting, setStarting] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const data = await syncSettingsApi.get();
      setHour(data.hour);
      setMinute(data.minute);
    } catch {
      toast('Не удалось загрузить настройки синхронизации', 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadSyncStatus = async () => {
    try {
      const data = await unifiedSyncApi.status();
      setSyncStatus(data);
      return data;
    } catch {
      return null;
    }
  };

  const stopPolling = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  };

  const startPolling = () => {
    stopPolling();
    pollRef.current = setInterval(async () => {
      const data = await loadSyncStatus();
      if (data && !data.in_progress) stopPolling();
    }, 3000);
  };

  useEffect(() => {
    load();
    loadSyncStatus().then((d) => {
      if (d?.in_progress) startPolling();
    });
    return stopPolling;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await syncSettingsApi.update({ hour, minute });
      toast('Время синхронизации сохранено', 'success');
    } catch {
      toast('Ошибка сохранения', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleSyncNow = async (forceFull: boolean) => {
    setStarting(true);
    try {
      await unifiedSyncApi.start(forceFull);
      toast('Синхронизация запущена', 'success');
      await loadSyncStatus();
      startPolling();
    } catch (e: unknown) {
      toast((e as Error)?.message || 'Не удалось запустить синхронизацию', 'error');
    } finally {
      setStarting(false);
    }
  };

  const inProgress = !!syncStatus?.in_progress;
  const lastResult = syncStatus?.last_result;

  return (
    <Box>
      <HelpText>
        Единая синхронизация данных из 1С в Mongo/Redis. Робот запускает её по расписанию; ниже — ручной запуск.
      </HelpText>

      <SectionCard title="Запуск">
        <Stack direction="row" flexWrap="wrap" gap={1}>
          <Button
            variant="contained"
            startIcon={inProgress ? <CircularProgress size={16} color="inherit" /> : <SyncIcon />}
            onClick={() => handleSyncNow(false)}
            disabled={inProgress || starting}
          >
            Синхронизировать сейчас
          </Button>
          <Button variant="outlined" startIcon={<BoltIcon />} onClick={() => handleSyncNow(true)} disabled={inProgress || starting}>
            Полная синхронизация
          </Button>
        </Stack>
        {inProgress && (
          <Alert severity="info" sx={{ mt: 2 }}>
            Синхронизация выполняется…
          </Alert>
        )}
        {!inProgress && lastResult && (
          <Alert severity={alertSeverity(lastResult.status)} sx={{ mt: 2 }}>
            <Stack spacing={0.5}>
              <span>
                Последний прогон:{' '}
                <Chip size="small" label={lastResult.status} color={statusColor(lastResult.status)} />{' '}
                {new Date(lastResult.timestamp).toLocaleString('ru-RU')}
              </span>
              {lastResult.errors?.length > 0 && <span>{lastResult.errors.join('; ')}</span>}
            </Stack>
          </Alert>
        )}
      </SectionCard>

      <SectionCard title="Ежедневный автозапуск">
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
          <TextField
            type="number"
            label="Час"
            size="small"
            inputProps={{ min: 0, max: 23 }}
            value={hour}
            onChange={(e) => setHour(Number(e.target.value))}
            sx={{ width: 100 }}
          />
          <TextField
            type="number"
            label="Минута"
            size="small"
            inputProps={{ min: 0, max: 59 }}
            value={minute}
            onChange={(e) => setMinute(Number(e.target.value))}
            sx={{ width: 100 }}
          />
          <Button variant="outlined" startIcon={<RefreshOutlinedIcon />} onClick={load} disabled={loading}>
            Обновить
          </Button>
          <Button variant="contained" startIcon={<SaveOutlinedIcon />} onClick={handleSave} disabled={saving}>
            Сохранить
          </Button>
        </Stack>
      </SectionCard>
    </Box>
  );
};

export default SyncSettings;
